import React, { useState, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import FilterableTable from "../common/FilterableTable";
import Accordion from "../common/Accordion";
import { casesApi } from "../../api";
import { Case, PredictedDiagnosis } from "../../api/cases";
import { useAuth } from "../../utils/auth";

// Extended interface to handle recommendations
interface ExtendedPredictedDiagnosis extends PredictedDiagnosis {
  isRecommendations?: boolean;
}
// React icons replaced with text/CSS alternatives

// Keyframes for spinner animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Responsive Icon Component
const Icon = styled.img<{ size?: 'small' | 'medium' | 'large' }>`
  display: block;
  flex-shrink: 0;
  
  ${props => props.size === 'small' && `
    width: 12px;
    height: 12px;
    
    @media (max-width: 480px) {
      width: 10px;
      height: 10px;
    }
  `}
  
  ${props => props.size === 'medium' && `
    width: 16px;
    height: 16px;
    
    @media (max-width: 480px) {
      width: 14px;
      height: 14px;
    }
  `}
  
  ${props => props.size === 'large' && `
    width: 24px;
    height: 24px;
    
    @media (max-width: 480px) {
      width: 20px;
      height: 20px;
    }
  `}
  
  ${props => !props.size && `
    width: 16px;
    height: 16px;
    
    @media (max-width: 480px) {
      width: 14px;
      height: 14px;
    }
  `}
`;

// Icon assets from Figma


const Container = styled.div`
  background: #ffffff;
  border-radius: 8px;
  width: 100%;
  min-height: calc(100vh - 32px);
  position: relative;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: 'SF Pro Display', 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    padding: 16px;
    gap: 20px;
    min-height: calc(100vh - 16px);
    border-radius: 0;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    gap: 16px;
    min-height: calc(100vh - 12px);
  }
`;

const Heading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const TitleText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 361px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Title = styled.h1`
  font-family: 'SF Pro Display', sans-serif;
  font-weight: 400;
  font-size: 22px;
  line-height: 28px;
  color: #2f3941;
  margin: 0;
  letter-spacing: 0.352px;
  
  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 26px;
  }
  
  @media (max-width: 480px) {
    font-size: 18px;
    line-height: 24px;
  }
`;

const Subtitle = styled.p`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  margin: 0;
  letter-spacing: -0.154px;
  
  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 18px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 16px;
  }
`;

const AddButton = styled.button`
  background: #8d4f1b;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #703815;
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 12px;
    gap: 6px;
  }
`;

const SearchFilter = styled.div`
  background: #ffffff;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const SearchInput = styled.input`
  background: #ffffff;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  padding: 10px 12px;
  width: 400px;
  height: 40px;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #c2c8cc;
  letter-spacing: -0.154px;
  
  &:focus {
    outline: none;
    border-color: #8d4f1b;
    color: #2f3941;
  }
  
  &::placeholder {
    color: #c2c8cc;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 10px;
  }
`;

const FilterButton = styled.button`
  background: #ffffff;
  border: 1px solid #8d4f1b;
  border-radius: 4px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #8d4f1b;
  letter-spacing: -0.154px;
  
  &:hover {
    background: rgba(141, 79, 27, 0.08);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #ffffff;
  min-width: 800px;
  
  @media (max-width: 768px) {
    min-width: 600px;
  }
  
  @media (max-width: 480px) {
    min-width: 500px;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  flex: 1;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  /* Hide scrollbars */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  
  &::-webkit-scrollbar {
    display: none; /* WebKit */
  }
  
  @media (max-width: 768px) {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    border-radius: 0;
    margin: 0 -16px;
    padding: 0 16px;
  }
  
  @media (max-width: 480px) {
    margin: 0 -12px;
    padding: 0 12px;
    display: none; /* Hide table on mobile */
  }
`;

const MobileCardContainer = styled.div`
  display: none;
  
  @media (max-width: 480px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 4px;
    
    /* Hide scrollbars */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    
    &::-webkit-scrollbar {
      display: none; /* WebKit */
    }
  }
`;

const MobileCard = styled.div`
  background: #ffffff;
  border: 1px solid #e9ebed;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
    border-color: #d8dcde;
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const MobileCardTitle = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 14px;
  line-height: 18px;
  color: #2f3941;
  letter-spacing: -0.154px;
  flex: 1;
`;

const MobileCardDate = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #68737d;
  letter-spacing: -0.154px;
`;

const MobileCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const MobileCardLabel = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #68737d;
  letter-spacing: -0.154px;
  min-width: 80px;
`;

const MobileCardValue = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #2f3941;
  letter-spacing: -0.154px;
  text-align: right;
  flex: 1;
`;

const MobileCardActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid #e9ebed;
  margin-top: 4px;
`;

const TableHeader = styled.thead`
  background: #ffffff;
`;

const TableHeaderCell = styled.th`
  background: #ffffff;
  padding: 26px 12px;
  text-align: left;
  border-bottom: 1px solid #d8dcde;
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
  
  @media (max-width: 768px) {
    padding: 20px 8px;
    font-size: 13px;
    line-height: 18px;
  }
  
  @media (max-width: 480px) {
    padding: 16px 6px;
    font-size: 12px;
    line-height: 16px;
  }
`;

const TableRow = styled.tr`
  background: #ffffff;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:active {
    background: #e9ecef;
  }
  
  @media (max-width: 768px) {
    &:hover {
      background: #f5f5f5;
    }
    
    &:active {
      background: #e9ecef;
    }
  }
  
  /* Add better touch targets for mobile */
  @media (max-width: 480px) {
    &:first-child td {
      border-top: 1px solid #e9ebed;
    }
  }
`;

const TableCell = styled.td`
  background: #ffffff;
  padding: 20px 12px;
  border-bottom: 1px solid #e9ebed;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
  
  @media (max-width: 768px) {
    padding: 16px 8px;
    font-size: 13px;
    line-height: 18px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 6px;
    font-size: 12px;
    line-height: 16px;
  }
`;

const ConfidenceText = styled.span`
  color: #87929d;
  
  @media (max-width: 480px) {
    display: block;
    margin-top: 2px;
  }
`;

const StatusTag = styled.div<{ status: 'completed' | 'pending' | 'reviewing' }>`
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#d4edda';
      case 'pending': return '#fff3cd';
      case 'reviewing': return '#cce5ff';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#155724';
      case 'pending': return '#856404';
      case 'reviewing': return '#0c5aa6';
      default: return '#495057';
    }
  }};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 590;
  display: inline-block;
  
  @media (max-width: 480px) {
    padding: 3px 6px;
    font-size: 11px;
  }
`;

const ActionLink = styled.button`
  background: none;
  border: none;
  color: #68737d;
  text-decoration: underline;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  transition: color 0.2s ease;
  padding: 8px 4px;
  margin: -8px -4px;
  
  &:hover {
    color: #2f3941;
  }
  
  &:active {
    color: #8d4f1b;
  }
  
  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 18px;
    padding: 10px 6px;
    margin: -10px -6px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 16px;
    padding: 12px 8px;
    margin: -12px -8px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: center;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const PaginationInfo = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  letter-spacing: -0.154px;
  
  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 18px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 16px;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 480px) {
    gap: 2px;
  }
`;

const PageButton = styled.button<{ active?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: none;
  background: ${props => props.active ? 'rgba(141, 79, 27, 0.08)' : 'transparent'};
  color: ${props => props.active ? '#2f3941' : '#68737d'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'SF Pro', sans-serif;
  font-weight: ${props => props.active ? '590' : '400'};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? 'rgba(141, 79, 27, 0.08)' : 'rgba(0, 0, 0, 0.04)'};
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 12px;
    line-height: 16px;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #8d4f1b;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  text-decoration: underline;
  margin-bottom: 24px;
  
  &:hover {
    color: #703815;
  }
`;

const CaseDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const CaseDetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const CaseDetailLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 768px) {
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const CaseDetailRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 768px) {
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const CaseImage = styled.div`
  width: 100%;
  height: 345px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
  
  /* Fallback background when no image */
  &:not([style*="background-image"]) {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  }
  
  &[style*="background-image"] {
    color: transparent; /* Hide placeholder text when image is present */
  }
  
  @media (max-width: 768px) {
    height: 280px;
  }
  
  @media (max-width: 480px) {
    height: 220px;
    border-radius: 6px;
  }
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  letter-spacing: -0.154px;
`;

const DetailValue = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
`;

const DetailValueLarge = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 22px;
  line-height: 28px;
  color: #2f3941;
  letter-spacing: 0.352px;
`;

const AISection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AISectionTitle = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  letter-spacing: -0.154px;
`;

const RecommendationsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
`;

const RecommendationsTitle = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  letter-spacing: -0.154px;
`;

const RecommendationsCard = styled.div`
  border: 1px solid #e9ebed;
  border-radius: 8px;
  padding: 16px;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RecommendationsText = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: #2f3941;
  letter-spacing: -0.154px;
  
  @media (max-width: 480px) {
    font-size: 15px;
    line-height: 21px;
  }
`;

const DifferentialCard = styled.div`
  border: 1px solid #e9ebed;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #ffffff;
`;

const DifferentialHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const DifferentialName = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 18px;
  line-height: 24px;
  color: #000000;
  letter-spacing: -0.45px;
`;

const DifferentialNameSmall = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  letter-spacing: -0.154px;
`;

const DifferentialPercentage = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 18px;
  line-height: 24px;
  color: #000000;
  letter-spacing: -0.45px;
`;

const DifferentialPercentageSmall = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  letter-spacing: -0.154px;
`;

const DifferentialDescription = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  letter-spacing: -0.000427656px;
  flex: 1;
  
  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 19px;
  }
`;

const RiskTag = styled.div<{ risk: 'high' | 'medium' | 'low' }>`
  background: ${props => {
    switch (props.risk) {
      case 'high': return '#058541';
      case 'medium': return '#bf5000';
      case 'low': return ' #cc3340';
      default: return '#cc3340';
    }
  }};
  color: #ffffff;
  padding: 2px 8px;
  border-radius: 2px;
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.000427656px;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const LinkText = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #8d4f1b;
  letter-spacing: -0.154px;
  text-decoration: underline;
  cursor: pointer;
  
  &:hover {
    color: #703815;
  }
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
  padding: 20px;
  
  /* Hide scrollbars */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  
  &::-webkit-scrollbar {
    display: none; /* WebKit */
  }
  
  @media (max-width: 768px) {
    padding: 0;
    align-items: flex-start;
  }
  
  @media (max-width: 480px) {
    padding: 0;
    align-items: flex-start;
  }
`;

const ModalContent = styled.div`
  background: #f8f9f9;
  border-radius: 8px;
  width: 90vw;
  max-width: 1400px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  
  /* Hide scrollbars */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  
  &::-webkit-scrollbar {
    display: none; /* WebKit */
  }
  
  @media (max-width: 768px) {
    width: 100vw;
    max-height: 100vh;
    border-radius: 0;
  }
  
  @media (max-width: 480px) {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const ModalHeader = styled.div`
  background: #ffffff;
  padding: 24px;
  border-bottom: 1px solid #e9ebed;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    gap: 8px;
    position: sticky;
    top: 0;
    z-index: 10;
    border-bottom: 2px solid #e9ebed;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #8d4f1b;
  letter-spacing: -0.154px;
  
  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 16px;
    gap: 2px;
  }
`;

const BreadcrumbSeparator = styled.div`
  width: 12px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    align-self: flex-end;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    align-self: flex-end;
  }
`;

const ProfileAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #e9ebed;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileText = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProfileName = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 24px;
  color: #2f3941;
  letter-spacing: -0.45px;
`;

const ProfileRole = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #68737d;
  letter-spacing: -0.000427656px;
`;

const MobileCloseButton = styled.button`
  display: none;
  background: #f0f0f0;
  border: 1px solid #d8dcde;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e8e8e8;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 480px) {
    display: flex;
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 10;
  }
`;

const ModalBody = styled.div`
  background: #ffffff;
  border-radius: 8px;
  margin: 24px;
  padding: 24px;
  position: relative;
  
  @media (max-width: 768px) {
    margin: 16px;
    padding: 16px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    margin: 12px;
    padding: 12px;
    border-radius: 4px;
  }
`;

const BackToMyCasesButton = styled.button`
  background: none;
  border: none;
  color: #68737d;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  
  &:hover {
    color: #2f3941;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #68737d;
  cursor: pointer;
  font-size: 24px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  &:hover {
    background: #f8f9fa;
    color: #2f3941;
  }
`;

// Add New Case Modal Styles
const AddNewCaseContainer = styled.div`
  background: #ffffff;
  border-radius: 8px;
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  margin: 0 20px;
  
  /* Hide scrollbars */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  
  &::-webkit-scrollbar {
    display: none; /* WebKit */
  }
  
  @media (max-width: 768px) {
    margin: 0;
    padding: 20px;
    gap: 20px;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  @media (max-width: 480px) {
    margin: 0;
    padding: 16px;
    gap: 16px;
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const StepperContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  gap: 0;
  max-width: 600px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 16px;
    padding: 0 8px;
  }
`;

const StepperStep = styled.div<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  position: relative;
  flex: 1;
  z-index: 2;
  
  @media (max-width: 480px) {
    gap: 6px;
    padding: 4px 0;
  }
`;

const StepperLine = styled.div<{ active?: boolean }>`
  height: 2px;
  background: ${props => props.active ? '#8d4f1b' : '#d8dcde'};
  flex: 1;
  transition: background-color 0.3s ease;
  min-width: 20px;
  
  @media (max-width: 480px) {
    height: 1px;
    min-width: 10px;
  }
`;

const StepperIcon = styled.div<{ active?: boolean; completed?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.completed ? '#22c55e' : props.active ? '#8d4f1b' : '#e9ebed'};
  color: ${props => props.completed ? '#ffffff' : props.active ? '#ffffff' : '#68737d'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.000427656px;
  position: relative;
  z-index: 3;
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
    font-size: 10px;
    line-height: 14px;
  }
`;

const StepperLabel = styled.div<{ active?: boolean }>`
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 14px;
  line-height: 20px;
  color: ${props => props.active ? '#2f3941' : '#68737d'};
  letter-spacing: -0.154px;
  text-align: center;
  white-space: nowrap;
  
  @media (max-width: 480px) {
    font-size: 11px;
    line-height: 14px;
    white-space: normal;
    max-width: 60px;
  }
`;

const StepperRow = styled.div<{ currentStep: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 0;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 12px;
    right: 12px;
    height: 2px;
    background: linear-gradient(to right, 
      ${props => props.currentStep >= 2 ? '#8d4f1b' : '#d8dcde'} 0%,
      ${props => props.currentStep >= 2 ? '#8d4f1b' : '#d8dcde'} 33.33%,
      ${props => props.currentStep >= 3 ? '#8d4f1b' : '#d8dcde'} 33.33%,
      ${props => props.currentStep >= 3 ? '#8d4f1b' : '#d8dcde'} 66.66%,
      ${props => props.currentStep >= 4 ? '#8d4f1b' : '#d8dcde'} 66.66%,
      ${props => props.currentStep >= 4 ? '#8d4f1b' : '#d8dcde'} 100%
    );
    z-index: 1;
    transform: translateY(-50%);
    
    @media (max-width: 480px) {
      height: 1px;
      left: 10px;
      right: 10px;
    }
  }
`;

const AnalyzingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  padding: 24px 0;
  
  @media (max-width: 768px) {
    gap: 20px;
    padding: 20px 0;
  }
  
  @media (max-width: 480px) {
    gap: 16px;
    padding: 16px 0;
  }
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  width: 100%;
  max-width: 300px;
  
  @media (max-width: 480px) {
    max-width: 280px;
  }
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  max-width: 300px;
  
  @media (max-width: 480px) {
    max-width: 280px;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #e9ebed;
  border-radius: 3px;
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div<{ percentage: number; color: string }>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props => props.color};
  border-radius: 3px;
  transition: width 0.3s ease;
  
  &.animating {
    animation: ${keyframes`
      0% { width: 0%; }
      50% { width: 75%; }
      100% { width: 50%; }
    `} 2s ease-in-out infinite;
  }
`;

const ProgressStatus = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #68737d;
  letter-spacing: -0.000427656px;
  
  @media (max-width: 480px) {
    font-size: 11px;
    line-height: 14px;
  }
`;

const ProgressText = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #68737d;
  letter-spacing: -0.000427656px;
  
  @media (max-width: 480px) {
    font-size: 11px;
    line-height: 14px;
  }
`;

const ProgressPercentage = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #68737d;
  letter-spacing: -0.000427656px;
  
  @media (max-width: 480px) {
    font-size: 11px;
    line-height: 14px;
  }
`;

const InfoAlert = styled.div`
  background: #f8f9f9;
  border: 1px solid #d8dcde;
  border-radius: 8px;
  padding: 20px 16px;
  width: 100%;
  max-width: 700px;
  
  @media (max-width: 768px) {
    padding: 16px 12px;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 12px 10px;
  }
`;

const CancelButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 12px;
  
  @media (max-width: 480px) {
    margin-top: 8px;
  }
`;

const CancelButton = styled.button`
  background: #ffffff;
  color: #2f3941;
  border: 1px solid #2f3941;
  border-radius: 4px;
  padding: 10px 16px;
  height: 40px;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(47, 57, 65, 0.08);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
    height: 36px;
    font-size: 13px;
  }
`;

const UploadContainer = styled.div`
  border: 1px dashed #e9ebed;
  border-radius: 8px;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #8d4f1b;
    background: #faf9f7;
  }
  
  @media (max-width: 768px) {
    padding: 40px 20px;
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 32px 16px;
    gap: 16px;
  }
`;

const UploadIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
`;

const UploadText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
`;

const UploadTitle = styled.div`
  font-family: 'SF Pro Display', sans-serif;
  font-weight: 400;
  font-size: 22px;
  line-height: 28px;
  color: #2f3941;
  letter-spacing: 0.352px;
  
  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 26px;
  }
  
  @media (max-width: 480px) {
    font-size: 18px;
    line-height: 24px;
  }
`;

const UploadSubtitle = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  letter-spacing: -0.154px;
  max-width: 422px;
  
  @media (max-width: 768px) {
    max-width: 100%;
    font-size: 13px;
    line-height: 18px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 16px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const PrimaryButton = styled.button`
  background: #8d4f1b;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  height: 40px;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  transition: all 0.2s ease;
  flex: 1;
  max-width: 200px;
  
  &:hover {
    background: #703815;
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &:disabled {
    background: #d8dcde;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 12px;
    max-width: 100%;
  }
`;

const SecondaryButton = styled.button`
  background: #ffffff;
  color: #8d4f1b;
  border: 1px solid #8d4f1b;
  border-radius: 4px;
  padding: 10px 16px;
  height: 40px;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  transition: all 0.2s ease;
  flex: 1;
  max-width: 200px;
  
  &:hover {
    background: rgba(141, 79, 27, 0.08);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 12px;
    max-width: 100%;
  }
`;

const AlertContainer = styled.div`
  background: #e9ebed;
  border: 1px solid #d8dcde;
  border-radius: 8px;
  padding: 20px 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    padding: 16px 12px;
    gap: 10px;
    margin-top: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 10px;
    gap: 8px;
    margin-top: 16px;
  }
`;

const AlertIcon = styled.div`
  width: 16px;
  height: 16px;
  margin-top: 4px;
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    width: 14px;
    height: 14px;
    margin-top: 2px;
  }
`;

const AlertContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AlertTitle = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
  
  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 18px;
  }
`;

const AlertText = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
  
  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 18px;
  }
`;

const AlertClose = styled.button`
  background: none;
  border: none;
  width: 28px;
  height: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  &:hover {
    background: #f8f9fa;
  }
  
  @media (max-width: 480px) {
    width: 24px;
    height: 24px;
  }
`;

const BackToMyCasesLink = styled.button`
  background: none;
  border: none;
  color: #68737d;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    color: #2f3941;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
    font-size: 13px;
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    display: none; /* Hide on mobile since we have the close button */
  }
`;

// Analysis Results Step Styled Components
const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 768px) {
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const ResultsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const ResultsTitle = styled.h2`
  font-family: 'SF Pro Display', sans-serif;
  font-weight: 400;
  font-size: 24px;
  line-height: 30px;
  color: #2f3941;
  margin: 0;
  letter-spacing: 0.352px;
  
  @media (max-width: 768px) {
    font-size: 22px;
    line-height: 28px;
  }
  
  @media (max-width: 480px) {
    font-size: 20px;
    line-height: 26px;
  }
`;

const CaseInfoAlert = styled.div`
  background: #f8f9f9;
  border: 1px solid #d8dcde;
  border-radius: 8px;
  padding: 16px;
  
  @media (max-width: 768px) {
    padding: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const CaseInfoTitle = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
  margin-bottom: 4px;
  
  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 18px;
  }
`;

const CaseInfoText = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
  
  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 18px;
  }
`;

const DifferentialResultCard = styled.div`
  border: 1px solid #e9ebed;
  border-radius: 8px;
  padding: 24px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 768px) {
    padding: 20px;
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    gap: 16px;
  }
`;

const DifferentialTop = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-items: flex-start;
  
  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const DifferentialNumber = styled.div`
  background: #e9ebed;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 14px;
  line-height: 20px;
  color: #2f3941;
  letter-spacing: -0.154px;
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
    font-size: 12px;
    line-height: 16px;
  }
`;

const DifferentialContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DifferentialDiagnosis = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const DiagnosisName = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 20px;
  line-height: 26px;
  color: #000000;
  letter-spacing: -0.45px;
  
  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 24px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 22px;
  }
`;

const DiagnosisPercentage = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 20px;
  line-height: 26px;
  color: #000000;
  letter-spacing: -0.45px;
  
  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 24px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 22px;
  }
`;

const DifferentialDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DiagnosisDescription = styled.div`
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #68737d;
  letter-spacing: -0.000427656px;
  flex: 1;
  
  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 16px;
  }
`;

const RiskBadge = styled.div<{ risk: 'high' | 'medium' | 'low' }>`
  background: ${props => {
    switch (props.risk) {
      case 'high': return '#cc3340';
      case 'medium': return '#bf5000';
      case 'low': return '#058541';
      default: return '#058541';
    }
  }};
  color: #ffffff;
  padding: 2px 8px;
  border-radius: 2px;
  font-family: 'SF Pro', sans-serif;
  font-weight: 590;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.000427656px;
  text-align: center;
  
  @media (max-width: 480px) {
    padding: 1px 6px;
    font-size: 11px;
    line-height: 14px;
  }
`;

const ResultsProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #e9ebed;
  border-radius: 3px;
  overflow: hidden;
`;

const ResultsProgressFill = styled.div<{ percentage: number; color: string }>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props => props.color};
  border-radius: 3px;
  transition: width 0.5s ease;
`;

const ResultsButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const SaveButton = styled.button`
  background: #8d4f1b;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  height: 40px;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #703815;
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 12px;
  }
`;

const StartOverButton = styled.button`
  background: #e9ebed;
  color: #2f3941;
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  height: 40px;
  cursor: pointer;
  font-family: 'SF Pro', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #d8dcde;
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  @media (max-width: 480px) {
    height: 36px;
    font-size: 13px;
    padding: 8px 12px;
  }
`;


const Cases: React.FC = () => {
  const { user } = useAuth();
  const [modalCaseId, setModalCaseId] = useState<string | null>(null);
  const [showAddNewCase, setShowAddNewCase] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch cases when component mounts
  useEffect(() => {
    const fetchCases = async () => {
      try {
        setLoading(true);
        const response = await casesApi.getMyCases();
        console.log(response)
        setCases(response);
      } catch (error: any) {
        console.error('Error fetching cases:', error);
        setError(error.message || 'Failed to fetch cases');
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, []);

  // Refresh cases after adding a new one
  const refreshCases = async () => {
    try {
      const response = await casesApi.getMyCases();
      setCases(response);
    } catch (error: any) {
      console.error('Error refreshing cases:', error);
    }
  };

  const generateCaseId = () => {
    return `DRM-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;
  };

  const handleViewDetails = (caseId: string) => {
    setModalCaseId(caseId);
  };

  const handleAddNewCase = () => {
    setShowAddNewCase(true);
  };

  const handleCloseModal = () => {
    setModalCaseId(null);
    // Refresh cases list after closing the modal
    refreshCases();
  };

  const handleCloseAddNewCase = () => {
    setShowAddNewCase(false);
    // Refresh cases list after closing the add new case modal
    refreshCases();
  };

  const CaseDetailModal = ({ caseId }: { caseId: string }) => {
    const caseData = cases.find(case_ => case_._id === caseId);
    if (!caseData) return null;

    // Format date
    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    // Get the first image URL
    const imageUrl = caseData.images[0]?.url || '';

    // Get primary diagnosis
    const primaryDiagnosis = caseData.predictedDiagnoses?.[0];
    
    // Separate diagnoses from recommendations
    // Look for items with isRecommendations flag or traditional detection methods
    const allDiagnoses = (caseData.predictedDiagnoses || []) as ExtendedPredictedDiagnosis[];
    const diagnoses = allDiagnoses.filter(item => !item.isRecommendations);
    const recommendationsItem = allDiagnoses.find(item => item.isRecommendations);
    
    // Fallback: if no isRecommendations flag, use original logic
    const fallbackDiagnoses = caseData.predictedDiagnoses?.slice(0, -1) || [];
    const fallbackRecommendations = caseData.predictedDiagnoses?.slice(-1)?.[0];
    
    const isFallbackRecommendations = fallbackRecommendations && 
      (fallbackRecommendations.label?.toLowerCase().includes('recommendation') || 
       fallbackRecommendations.label?.toLowerCase().includes('suggest') ||
       fallbackRecommendations.confidence === 0);
    
    const actualDiagnoses = recommendationsItem ? diagnoses : 
                           (isFallbackRecommendations ? fallbackDiagnoses : caseData.predictedDiagnoses || []);
    const actualRecommendations = recommendationsItem || 
                                 (isFallbackRecommendations ? fallbackRecommendations : null);

    return (
      <ModalOverlay onClick={handleCloseModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <MobileCloseButton onClick={handleCloseModal}>
              ×
              <Title></Title>
            </MobileCloseButton>
          </ModalHeader>
          
          <ModalBody>
            <BackToMyCasesButton onClick={handleCloseModal} aria-label="Back">
              ← 
              Back
            </BackToMyCasesButton>
            
            <CaseDetailGrid>
              <CaseDetailLeft>
                <CaseImage style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : 'none' }}>
                  {!imageUrl && 'No Image Available'}
                </CaseImage>
                <DetailSection>
                  <DetailItem>
                    <DetailLabel>Submitted by:</DetailLabel>
                    <DetailValue>
                      {user?.role === 'physician' || user?.role === 'specialist' 
                        ? user.name 
                        : typeof caseData.createdBy === 'object' 
                          ? caseData.createdBy.name 
                          : 'Unknown'
                      }
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Submission Date:</DetailLabel>
                    <DetailValue>{formatDate(caseData.createdAt)}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Clinical Information:</DetailLabel>
                    <DetailValue>{caseData.doctorNote}</DetailValue>
                  </DetailItem>
                </DetailSection>
              </CaseDetailLeft>
              
              <CaseDetailRight>
                <AISection>
                  <AISectionTitle>AI Differentials:</AISectionTitle>
                  {actualDiagnoses && actualDiagnoses.length > 0 ? (
                    <>
                      <DifferentialCard>
                        <div>
                          <DifferentialHeader>
                            <DifferentialName>{actualDiagnoses[0].label}</DifferentialName>
                            <DifferentialPercentage>
                              {Math.round(actualDiagnoses[0].confidence)}%
                            </DifferentialPercentage>
                          </DifferentialHeader>
                          <DifferentialDetails>
                            <Accordion title="View Reasoning" defaultOpen={false}>
                              <DifferentialDescription>
                                {actualDiagnoses[0].reasoning}
                              </DifferentialDescription>
                            </Accordion>
                            <RiskTag risk={
                              actualDiagnoses[0].confidence > 70 ? 'high' : 
                              actualDiagnoses[0].confidence > 40 ? 'medium' : 'low'
                            }>
                              {actualDiagnoses[0].confidence > 70 ? 'High' : 
                               actualDiagnoses[0].confidence > 40 ? 'Medium' : 'Low'}
                            </RiskTag>
                          </DifferentialDetails>
                        </div>
                        <ProgressBar>
                          <ProgressFill 
                            percentage={Math.round(actualDiagnoses[0].confidence)} 
                            color={
                              actualDiagnoses[0].confidence > 70 ? '#038153' : 
                              actualDiagnoses[0].confidence > 40 ? '#de701d' : '#00a656'
                            }
                          />
                        </ProgressBar>
                      </DifferentialCard>
                      
                      {actualDiagnoses.length > 1 && (
                        <TwoColumnGrid>
                          {actualDiagnoses.slice(1, 3).map((diagnosis, index) => (
                            <DifferentialCard key={index}>
                              <div>
                                <DifferentialHeader>
                                  <DifferentialNameSmall>{diagnosis.label}</DifferentialNameSmall>
                                  <DifferentialPercentageSmall>
                                    {Math.round(diagnosis.confidence )}%
                                  </DifferentialPercentageSmall>
                                </DifferentialHeader>
                                <DifferentialDetails>
                                  <Accordion title="View Reasoning" defaultOpen={false}>
                                    <DifferentialDescription>{diagnosis.reasoning}</DifferentialDescription>
                                  </Accordion>
                                  <RiskTag risk={
                                    diagnosis.confidence > 70 ? 'high' : 
                                    diagnosis.confidence > 40 ? 'medium' : 'low'
                                  }>
                                    {diagnosis.confidence > 70 ? 'High' : 
                                     diagnosis.confidence > 40 ? 'Medium' : 'Low'}
                                  </RiskTag>
                                </DifferentialDetails>
                              </div>
                              <ProgressBar>
                                <ProgressFill 
                                  percentage={Math.round(diagnosis.confidence)} 
                                  color={
                                    diagnosis.confidence > 70 ? '#038153' : 
                                    diagnosis.confidence > 40 ? '#de701d' : '#00a656'
                                  }
                                />
                              </ProgressBar>
                            </DifferentialCard>
                          ))}
                        </TwoColumnGrid>
                      )}
                    </>
                  ) : (
                    <div>No diagnosis available yet</div>
                  )}
                </AISection>
                
                {actualRecommendations && (
                  <RecommendationsSection>
                    <RecommendationsTitle>Recommendations:</RecommendationsTitle>
                    <RecommendationsCard>
                      <RecommendationsText>
                        {actualRecommendations.reasoning || actualRecommendations.label}
                      </RecommendationsText>
                    </RecommendationsCard>
                  </RecommendationsSection>
                )}
                
                <DetailSection>
                  <DetailItem>
                    <DetailLabel>Primary Diagnosis:</DetailLabel>
                    <DetailValueLarge>
                      {actualDiagnoses?.[0]?.label || caseData.doctorDiagnosis}
                    </DetailValueLarge>
                  </DetailItem>
                  
                  <DetailItem>
                    <DetailLabel>Review Status:</DetailLabel>
                    <DetailValue>
                      {caseData.reviewStatus === 'approved' ? 'Completed' : 
                       caseData.reviewStatus === 'rejected' ? 'Rejected' : 'Pending'}
                    </DetailValue>
                  </DetailItem>
                  {caseData.reviewNote && (
                    <DetailItem>
                      <DetailLabel>Review Notes:</DetailLabel>
                      <DetailValue>{caseData.reviewNote}</DetailValue>
                    </DetailItem>
                  )}
                </DetailSection>
              </CaseDetailRight>
            </CaseDetailGrid>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    );
  };

  const AddNewCaseModal = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [uploadedImages, setUploadedImages] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [clinicalInfo, setClinicalInfo] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [analysisResults, setAnalysisResults] = useState<any>(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [caseId, setCaseId] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // File upload handlers
    const handleDrag = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === "dragenter" || e.type === "dragover") {
        setDragActive(true);
      } else if (e.type === "dragleave") {
        setDragActive(false);
      }
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      
      const files = Array.from(e.dataTransfer.files);
      handleFiles(files);
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      handleFiles(files);
    };

    const handleFiles = (files: File[]) => {
      // Filter for image files only
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      
      // Limit to 1 image maximum
      const limitedFiles = imageFiles.slice(0, 1);
      
      setUploadedImages(limitedFiles);
      
      // Create preview URLs
      setImagePreviews([]); // Clear existing previews
      limitedFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            setImagePreviews([e.target!.result as string]);
          }
        };
        reader.readAsDataURL(file);
      });
    };

    const replaceImage = () => {
      // Clear current images
      setUploadedImages([]);
      setImagePreviews([]);
      // Open file picker
      fileInputRef.current?.click();
    };

    const uploadToCloudinary = async (file: File): Promise<string> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', "ml_default");
      try {
        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
          {
            method: 'POST',
            body: formData
          }
        );
        const data = await response.json();
        if (!response.ok || !data.secure_url) {
          throw new Error(data.error?.message || 'Image upload failed. Please try again.');
        }
        return data.secure_url;
      } catch (error: any) {
        console.error('Error uploading to Cloudinary:', error);
        throw error;
      }
    };

    const handleNext = async () => {
      if (currentStep === 1) {
        if (uploadedImages.length === 0) {
          alert('Please upload at least one image');
          return;
        }
        setCurrentStep(2);
      } else if (currentStep === 2) {
        setCurrentStep(3);
        setIsAnalyzing(true);
        try {
          // Upload images to Cloudinary
          setIsUploading(true);
          const imageUrls: string[] = [];
          for (const file of uploadedImages) {
            try {
              const url = await uploadToCloudinary(file);
              imageUrls.push(url);
            } catch (uploadError: any) {
              setIsUploading(false);
              setIsAnalyzing(false);
              alert('Error uploading image: ' + (uploadError?.message || 'Please check your image and try again.'));
              return; // Stop the flow if image upload fails
            }
          }
          setIsUploading(false);
          // Create case with the backend
          const caseData = {
            images: imageUrls.map(url => ({ url })),
            age: 30, // TODO: Get from form
            sex: 'Male' as const, // TODO: Get from form  
            doctorDiagnosis: 'Initial Assessment',
            doctorNote: clinicalInfo || 'AI Analysis Request',
          };

          const createResponse = await casesApi.createCase(caseData);
          const { jobId, case: createdCase } = createResponse;
          
          // Set the case ID from the created case
          setCaseId(createdCase._id);
          
          // Poll for diagnosis completion every 3 seconds
          const diagnosisResult = await casesApi.pollDiagnosis(jobId, 30, 3000);
          console.log(diagnosisResult)
          // Transform the backend response to match the frontend format
          const transformedResults = {

            primaryDiagnosis: diagnosisResult.predictedDiagnoses?.[0]?.label || 'No diagnosis available',
            confidence: Math.round((diagnosisResult.predictedDiagnoses?.[0]?.confidence || 0) * 100) + '%',
            differentials: diagnosisResult.predictedDiagnoses?.map((diagnosis: { label: any; confidence: number; reasoning?: string; }, index: any) => ({
              name: diagnosis.label,
              percentage: Math.round(diagnosis.confidence),
              description: diagnosis.reasoning || `This diagnosis has a confidence level of ${Math.round(diagnosis.confidence)}%. The AI model identified key features that match this condition.`,
              risk: diagnosis.confidence > 0.7 ? 'high' : diagnosis.confidence > 0.4 ? 'medium' : 'low',
              color: diagnosis.confidence > 0.7 ? '#cc3340' : diagnosis.confidence > 0.4 ? '#de701d' : '#299c66'
            })) || []
          };
          
          setAnalysisResults(transformedResults);
          setIsAnalyzing(false);
          setCurrentStep(4); // Move to results step
          
        } catch (error) {
          console.error('Error analyzing case:', error);
          setIsAnalyzing(false);
          setIsUploading(false);
          
          // Show error message to user
          alert('Error processing case: ' + (error instanceof Error ? error.message : 'Unknown error occurred. Please try again.'));
          
          // Reset to step 2 so user can try again
          setCurrentStep(2);
        }
      }
    };

    const handlePrevious = () => {
      if (currentStep > 1) {
        setCurrentStep(currentStep - 1);
      }
    };

    const handleSkip = () => {
      if (currentStep === 2) {
        handleNext();
      }
    };

    const handleSubmit = async () => {
      if (currentStep === 1) {
        setCurrentStep(2);
      } else if (currentStep === 2) {
        setCurrentStep(3);
        setIsAnalyzing(true);
        
        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 5000));
          
          // Set analysis results
          setAnalysisResults({
            primaryDiagnosis: 'Actinic Keratosis',
            confidence: '85%',
            differentials: [
              {
                name: 'Actinic Keratosis',
                percentage: 85,
                description: 'The lesion shows characteristic features of actinic keratosis including rough, scaly texture with irregular borders. The location and patient demographics are consistent with chronic sun exposure. The hyperkeratotic surface and surrounding erythema are typical findings.',
                risk: 'medium',
                color: '#f59e0b'
              },
              {
                name: 'Basal Cell Carcinoma',
                percentage: 65,
                description: 'The lesion presents with some features suggestive of basal cell carcinoma including pearly appearance and possible telangiectasias. The nodular appearance and central depression could indicate early BCC development.',
                risk: 'high',
                color: '#dc2626'
              },
              {
                name: 'Seborrheic Keratosis',
                percentage: 45,
                description: 'The lesion shows some characteristics of seborrheic keratosis with its warty, stuck-on appearance. However, the irregular borders and lack of typical horn cysts make this diagnosis less likely.',
                risk: 'low',
                color: '#16a34a'
              }
            ]
          });
          
          setIsAnalyzing(false);
          setCurrentStep(4);
          
          // Auto-close modal after 3 seconds and refresh cases
          setTimeout(async () => {
            await refreshCases();
            handleCloseAddNewCase();
            setSuccessMessage('Case successfully analyzed and saved to your cases!');
            setIsSuccessVisible(true);
            setTimeout(() => setIsSuccessVisible(false), 3000);
          }, 3000);
          
        } catch (error) {
          console.error('Analysis failed:', error);
          setIsAnalyzing(false);
        }
      }
    };

    const handleStartOver = () => {
      setCurrentStep(1);
      setUploadedImages([]);
      setImagePreviews([]);
      setClinicalInfo('');
      setIsAnalyzing(false);
      setAnalysisResults(null);
      setCaseId('');
    };

    const renderStepContent = () => {
      switch (currentStep) {
        case 1:
          return (
            <div>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Title>Upload Image(s)</Title>
                <Subtitle>Upload a clear image of the skin condition for AI analysis</Subtitle>
              </div>

              {/* Conditional rendering based on upload state */}
              {imagePreviews.length === 0 ? (
                <>
                  <UploadContainer
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    style={{
                      border: dragActive ? '2px dashed #1f73b7' : '2px dashed #d8dcde',
                      backgroundColor: dragActive ? '#f0f8ff' : '#ffffff',
                      cursor: 'pointer'
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileInput}
                      style={{ display: 'none' }}
                    />
                    <UploadIcon>
                      ↑
                    </UploadIcon>
                    <UploadText>
                      <UploadTitle>Click or drag image into this area to upload</UploadTitle>
                      <UploadSubtitle>Support for JPEG, PNG files up to 10MB. Single image only.</UploadSubtitle>
                    </UploadText>
                  </UploadContainer>

                  <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
                    <PrimaryButton onClick={() => fileInputRef.current?.click()}>
                      Choose Files
                    </PrimaryButton>
                    <SecondaryButton
                      onClick={() => {
                        // Open camera for direct capture (mobile only)
                        if (fileInputRef.current) {
                          fileInputRef.current.setAttribute('capture', 'environment');
                          fileInputRef.current.click();
                          // Reset capture after click to allow normal file selection later
                          setTimeout(() => fileInputRef.current?.removeAttribute('capture'), 1000);
                        }
                      }}
                    >
                      Use Camera
                    </SecondaryButton>
                  </div>
                </>
              ) : (
                <>
                  {/* Uploaded Images in Center */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    marginTop: '24px' 
                  }}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr', 
                      gap: '16px',
                      width: '100%',
                      maxWidth: '300px'
                    }}>
                      {imagePreviews.map((preview, index) => (
                        <div key={index} style={{ position: 'relative' }}>
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '200px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '2px solid #8D4F1B'
                            }}
                          />
                          <button
                            onClick={() => replaceImage()}
                            style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              background: '#cc3340',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '16px'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
          
        case 2:
          return (
            <div>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Title>Clinical Scenario Details</Title>
                <Subtitle>Provide additional clinical context to improve AI analysis accuracy</Subtitle>
              </div>
              
              <div style={{ marginBottom: '24px' }}>
                <textarea
                  value={clinicalInfo}
                  onChange={(e) => setClinicalInfo(e.target.value)}
                  style={{
                    width: '100%',
                    height: '184px',
                    padding: '12px',
                    border: '1px solid #d8dcde',
                    borderRadius: '4px',
                    resize: 'none',
                    fontFamily: 'SF Pro, sans-serif',
                    fontSize: '14px',
                    lineHeight: '20px'
                  }}
                  placeholder="Describe the lesion characteristics, patient history, symptoms, duration, location, changes over time, associated symptoms, family history, medications, and any other relevant clinical information..."
                />
              </div>
              
              <AlertContainer>
                <AlertIcon>
                  ℹ
                </AlertIcon>
                <AlertContent>
                  <AlertTitle>Why provide clinical context?</AlertTitle>
                  <AlertText>
                    • Improves diagnostic accuracy by 25%<br />
                    • Helps differentiate between similar-looking conditions<br />
                    • Provides more relevant differential diagnoses<br />
                    • Assists in ruling out certain conditions
                  </AlertText>
                </AlertContent>
                <AlertClose>
                  ×
                </AlertClose>
              </AlertContainer>
            </div>
          );
          
        case 3:
          return (
            <div>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Title>Analyzing Image</Title>
                <Subtitle>Our AI is processing your image and generating differential diagnoses</Subtitle>
              </div>
              
              {isAnalyzing ? (
                <AnalyzingContainer>
                  <ProgressSection>
                    <ProgressBarContainer>
                      <ProgressBar>
                        <ProgressFill 
                          percentage={isUploading ? 30 : 70} 
                          color="#038153" 
                          className="animating"
                        />
                      </ProgressBar>
                    </ProgressBarContainer>
                    <ProgressStatus>
                      <ProgressText>
                        {isUploading ? 'Uploading images...' : 'Analyzing with AI...'}
                      </ProgressText>
                      <ProgressPercentage>{isUploading ? '30%' : '70%'}</ProgressPercentage>
                    </ProgressStatus>
                  </ProgressSection>
                  
                  <InfoAlert>
                    <AlertContent>
                      <AlertText>
                        {isUploading 
                          ? 'Securely uploading your images to our cloud storage...'
                          : 'Our AI model is analyzing thousands of dermatological features to provide accurate differential diagnoses. This may take 30-60 seconds.'
                        }
                      </AlertText>
                    </AlertContent>
                  </InfoAlert>
                  
                  <CancelButtonContainer>
                    <CancelButton onClick={handleCloseAddNewCase}>
                      Cancel Analysis
                    </CancelButton>
                  </CancelButtonContainer>
                </AnalyzingContainer>
              ) : null}
            </div>
          );
          
        case 4:
          return (
            <ResultsContainer>
              <ResultsHeader>
                <ResultsTitle>Differential Diagnosis Results</ResultsTitle>
              </ResultsHeader>
              
              <CaseInfoAlert>
                <CaseInfoText>
                  Analysis completed on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()} | AI-powered diagnosis
                </CaseInfoText>
              </CaseInfoAlert>
              
              {analysisResults?.differentials?.map((differential: any, index: number) => (
                <DifferentialResultCard key={index}>
                  <DifferentialTop>
                    <DifferentialNumber>{index + 1}</DifferentialNumber>
                    <DifferentialContent>
                      <DifferentialDiagnosis>
                        <DiagnosisName>{differential.name}</DiagnosisName>
                        <DiagnosisPercentage>{differential.percentage}%</DiagnosisPercentage>
                      </DifferentialDiagnosis>
                      <DifferentialDetails>
                        <Accordion title="View Reasoning" defaultOpen={false}>
                          <DiagnosisDescription>{differential.description}</DiagnosisDescription>
                        </Accordion>
                        <RiskBadge risk={differential.risk}>
                          {differential.risk === 'high' ? 'High' : 
                           differential.risk === 'medium' ? 'Medium' : 'Low'}
                        </RiskBadge>
                      </DifferentialDetails>
                    </DifferentialContent>
                  </DifferentialTop>
                  <ResultsProgressBar>
                    <ResultsProgressFill 
                      percentage={differential.percentage} 
                      color={differential.color}
                    />
                  </ResultsProgressBar>
                </DifferentialResultCard>
              ))}
              
              <ResultsButtonContainer>
                <StartOverButton onClick={handleStartOver}>
                  Analyze Another Case
                </StartOverButton>
              </ResultsButtonContainer>
            </ResultsContainer>
          );
          
        default:
          return null;
      }
    };

    return (
      <ModalOverlay onClick={handleCloseAddNewCase}>
        <AddNewCaseContainer 
          onClick={(e) => e.stopPropagation()}
          onScroll={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <MobileCloseButton onClick={handleCloseAddNewCase}>
            ×
          </MobileCloseButton>
          <BackToMyCasesLink onClick={handleCloseAddNewCase}>
            ← 
            Back to My Cases
          </BackToMyCasesLink>
          
          <StepperContainer>
            <StepperRow currentStep={currentStep}>
              <StepperStep>
                <StepperIcon active={currentStep >= 1} completed={currentStep > 1}>
                  {currentStep > 1 ? '✓' : '1'}
                </StepperIcon>
                <StepperLabel active={currentStep === 1}>Upload Image</StepperLabel>
              </StepperStep>
              
              <StepperStep>
                <StepperIcon active={currentStep >= 2} completed={currentStep > 2}>
                  {currentStep > 2 ? '✓' : '2'}
                </StepperIcon>
                <StepperLabel active={currentStep === 2}>Clinical Information</StepperLabel>
              </StepperStep>
              
              <StepperStep>
                <StepperIcon active={currentStep >= 3} completed={currentStep > 3}>
                  {currentStep > 3 ? '✓' : '3'}
                </StepperIcon>
                <StepperLabel active={currentStep === 3}>AI Analysis</StepperLabel>
              </StepperStep>
              
              <StepperStep>
                <StepperIcon active={currentStep >= 4}>4</StepperIcon>
                <StepperLabel active={currentStep === 4}>Results</StepperLabel>
              </StepperStep>
            </StepperRow>
          </StepperContainer>
          
          {renderStepContent()}
          
          <ButtonRow>
            {currentStep === 1 && uploadedImages.length > 0 ? (
              <>
                <SecondaryButton onClick={replaceImage}>
                  Replace Image
                </SecondaryButton>
                <PrimaryButton onClick={handleNext}>
                  Proceed
                </PrimaryButton>
              </>
            ) : currentStep === 1 && uploadedImages.length === 0 ? (
              <PrimaryButton
                onClick={handleNext}
                disabled={true}
                style={{ width: '100%' }}
              >
                Please Upload an Image
              </PrimaryButton>
            ) : currentStep === 2 ? (
              <PrimaryButton
                onClick={handleNext}
                style={{ width: '100%' }}
              >
                Analyze
              </PrimaryButton>
            ) : null}
          </ButtonRow>
        </AddNewCaseContainer>
      </ModalOverlay>
    );
  };

  // Transform case data for table display
  const transformCasesForTable = (cases: Case[]) => {
    return cases.map(case_ => {
      // Separate diagnoses from recommendations (same logic as in modal)
      const allDiagnoses = (case_.predictedDiagnoses || []) as ExtendedPredictedDiagnosis[];
      const diagnoses = allDiagnoses.filter(item => !item.isRecommendations);
      const recommendationsItem = allDiagnoses.find(item => item.isRecommendations);
      
      // Fallback: if no isRecommendations flag, use original logic
      const fallbackDiagnoses = case_.predictedDiagnoses?.slice(0, -1) || [];
      const fallbackRecommendations = case_.predictedDiagnoses?.slice(-1)?.[0];
      
      const isFallbackRecommendations = fallbackRecommendations && 
        (fallbackRecommendations.label?.toLowerCase().includes('recommendation') || 
         fallbackRecommendations.label?.toLowerCase().includes('suggest') ||
         fallbackRecommendations.confidence === 0);
      
      const actualDiagnoses = recommendationsItem ? diagnoses : 
                             (isFallbackRecommendations ? fallbackDiagnoses : case_.predictedDiagnoses || []);
      const primaryDiagnosis = actualDiagnoses?.[0];
      
      return {
        _id: case_._id,
        date: new Date(case_.createdAt).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }),
        aiDifferential: primaryDiagnosis?.label || 'Processing...',
        confidence: primaryDiagnosis ? Math.round(primaryDiagnosis.confidence) : 0,
        primaryDiagnosis: case_.doctorDiagnosis,
        finalDiagnosis: primaryDiagnosis?.label || '-',
        reviewStatus: case_.reviewStatus || 'pending',
        diagnosisStatus: case_.diagnosisStatus
      };
    });
  };

  return (
    <Container>
      {!showAddNewCase ? (
        <>
          {error && (
            <div style={{ 
              padding: '20px', 
              backgroundColor: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#c00'
            }}>
              <strong>Error loading cases:</strong> {error}
            </div>
          )}
          <FilterableTable
            title="My Cases"
            description="Manage and review your uploaded cases and diagnoses"
            data={transformCasesForTable(cases)}
            loading={loading}
            columns={[
              { 
                key: 'date', 
                title: 'Date',
                render: (value) => value
              },
              { 
                key: 'aiDifferential', 
                title: 'AI Differential',
                render: (value, item) => (
                  <div>
                    {value} {item.confidence > 0 && <ConfidenceText>| Confidence: {item.confidence}%</ConfidenceText>}
                  </div>
                )
              },
              { 
                key: 'primaryDiagnosis', 
                title: 'Primary Diagnosis',
                render: (value) => value
              },
              { 
                key: 'finalDiagnosis', 
                title: 'Final Diagnosis',
                render: (value) => value
              },
              { 
                key: 'reviewStatus', 
                title: 'Review Status',
                render: (value) => (
                  <StatusTag status={value}>
                    {value === 'approved' ? 'Completed' : 
                     value === 'rejected' ? 'Rejected' : 'Pending'}
                  </StatusTag>
                )
              },
              { 
                key: 'actions', 
                title: 'Actions',
                render: (_, item) => (
                  <ActionLink onClick={(e) => { e.stopPropagation(); handleViewDetails(item._id); }}>
                    View Details
                  </ActionLink>
                )
              }
            ]}
            searchPlaceholder="Search cases by diagnosis or status"
            onRowClick={(item) => handleViewDetails(item._id)}
            searchFields={['aiDifferential', 'primaryDiagnosis', 'finalDiagnosis']}
            mobileCardConfig={{
              titleKey: 'aiDifferential',
              dateKey: 'date',
              rows: [
                { label: 'Primary Diagnosis', key: 'primaryDiagnosis' },
                { label: 'Final Diagnosis', key: 'finalDiagnosis' },
                { label: 'Review Status', key: 'reviewStatus' },
                { label: 'Confidence', key: 'confidence' }
              ]
            }}
            headerActions={
              <AddButton onClick={handleAddNewCase}>
                +
                Add New Case
              </AddButton>
            }
          />
        </>
      ) : null}
      
      {modalCaseId !== null && <CaseDetailModal caseId={modalCaseId} />}
      {showAddNewCase && <AddNewCaseModal />}
    </Container>
  );
};

export default Cases;
